import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, Lock } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { api } from "@/lib/api";
import { toast } from "sonner";

export const LanguageSelector = ({ languages, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [requested, setRequested] = useState({});
  const selected = languages.find((l) => l.id === value);
  const { available, unavailable } = useMemo(() => ({
    available: languages.filter((l) => l.available).sort((a, b) => b.popularity - a.popularity),
    unavailable: languages.filter((l) => !l.available),
  }), [languages]);

  const request = async (lang) => {
    try {
      await api.post("/language-requests", { language_id: lang.id });
      setRequested((r) => ({ ...r, [lang.id]: true }));
      toast.success(`Thanks — we've noted interest in ${lang.english_name}.`);
    } catch {
      toast.error("Couldn't record that request just now.");
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" role="combobox" aria-expanded={open} aria-label="Song language" data-testid="create-wizard-language-select"
          className="field flex items-center justify-between text-left">
          {selected ? (
            <span>{selected.english_name}{selected.native_name !== selected.english_name && <> · <bdi className="text-[var(--plum-muted)]" lang={selected.code}>{selected.native_name}</bdi></>}</span>
          ) : <span className="text-[var(--plum-muted)]">Choose a song language</span>}
          <ChevronsUpDown className="h-4 w-4 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] max-w-[92vw] p-0" align="start">
        <Command filter={(v, s) => (v.toLowerCase().includes(s.toLowerCase()) ? 1 : 0)}>
          <CommandInput placeholder="Search languages (English or native name)…" data-testid="language-search-input" />
          <CommandList className="max-h-[340px]">
            <CommandEmpty>No language matches that search.</CommandEmpty>
            <CommandGroup heading="Available now">
              {available.map((l) => (
                <CommandItem key={l.id} value={`${l.english_name} ${l.native_name} ${l.code}`} data-testid={`language-option-${l.id}`}
                  onSelect={() => { onChange(l.id); setOpen(false); }} className="py-2.5">
                  <Check className={`h-4 w-4 ${value === l.id ? "opacity-100" : "opacity-0"}`} />
                  <span>{l.english_name}</span>
                  <span className="bidi-isolate ml-auto text-[var(--plum-muted)]" lang={l.code}>{l.native_name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandGroup heading="Not yet available">
              {unavailable.map((l) => (
                <CommandItem key={l.id} value={`${l.english_name} ${l.native_name} ${l.code}`} data-testid={`language-option-${l.id}`}
                  onSelect={() => !requested[l.id] && request(l)} className="py-2.5 text-[var(--plum-muted)]">
                  <Lock className="h-3.5 w-3.5" />
                  <span>{l.english_name}</span>
                  <span className="bidi-isolate text-xs" lang={l.code}>{l.native_name}</span>
                  <span className="ml-auto text-xs text-[var(--rose)]">{requested[l.id] ? "Requested" : "Notify me"}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
